// Main - entry point that wires everything together

import { Renderer } from './renderer.js';
import { Input, isTypingTarget } from './input.js';
import { Game } from './game.js';
import { startVersionCheck } from './version-check.js';
import { getActiveRelics, setRelicActive } from './storage.js';
import { isTelemetryOptedOut, setTelemetryOptOut } from './session-logger.js';
import { toggleLang } from './i18n.js';

// Fetch the build tag so session logs can attribute balance problems to a
// specific commit. Swallows failures — if version.json is missing or the
// file:// origin blocks fetch, we fall back to 'unknown' without crashing.
async function loadBuildInfo() {
  try {
    const res = await fetch('./version.json', { cache: 'no-store' });
    if (!res.ok) return { commit: 'unknown', short: 'unknown', version: null };
    const data = await res.json();
    return {
      commit: data.commit || 'unknown',
      short: data.short || (data.commit ? data.commit.slice(0, 7) : 'unknown'),
      version: data.version || null,
    };
  } catch (e) {
    return { commit: 'unknown', short: 'unknown', version: null };
  }
}

async function init() {
  const canvas = document.getElementById('game');
  if (!canvas) {
    console.error('Canvas element #game not found');
    return;
  }

  const build = await loadBuildInfo();
  const renderer = new Renderer(canvas);

  // AI demo mode: open index.html?ai=smart (or greedy, runner, etc.)
  // to watch an AI policy play the game with full rendering.
  const params = new URLSearchParams(window.location.search);
  const aiPolicyName = params.get('ai');

  let input;
  let aiController = null;

  if (aiPolicyName) {
    const { POLICIES } = await import('./sim/policies.js');
    const { AIInput } = await import('./ai-input.js');
    const { AIController } = await import('./ai-controller.js');
    const factory = POLICIES[aiPolicyName];
    if (factory) {
      // &aiParams=<json> tunes a parametrised bot (e.g. ?ai=psmart, evolved
      // in ~/dev/corovans-ai).
      const policy = factory(JSON.parse(params.get('aiParams') || '{}'));
      input = new AIInput();
      aiController = new AIController(policy, input);
      console.log(`[ai] AI demo mode: policy=${aiPolicyName}`);
    } else {
      console.warn(`[ai] Unknown policy "${aiPolicyName}", falling back to human input`);
    }
  }

  if (!input) {
    input = new Input(canvas);
  }

  const game = new Game(renderer, input, { build });

  if (aiController) {
    game._aiController = aiController;
    // Speed control: 1-4 keys set game speed (1x, 2x, 4x, 8x).
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Digit1') game._timeScale = 1;
      else if (e.code === 'Digit2') game._timeScale = 2;
      else if (e.code === 'Digit3') game._timeScale = 4;
      else if (e.code === 'Digit4') game._timeScale = 8;
      else if (e.code === 'Digit5') game._timeScale = 16;
      else if (e.code === 'Digit6') game._timeScale = 32;
    });
  }

  // Guard brain: index.html?brain=surround[&brainParams={"ringRadius":90}]
  // swaps the guard tactics (see guard-brain.js). Combine with ?ai=smart to
  // watch an evolved brain fight a bot. Without ?brain= the 1.7 build plays
  // the evolved v4 tactics (?brain=builtin = the old built-in guard AI).
  const { GUARD_BRAINS, GUARD_V4 } = await import('./guard-brain.js');
  const brainName = params.get('brain');
  if (brainName) {
    // Experimental guard tactics must not reach production telemetry —
    // those sessions would skew the balance data (see session-logger.js).
    globalThis.KOROVANY_TELEMETRY_URL = null;
    const factory = GUARD_BRAINS[brainName];
    if (factory) {
      game._guardBrain = factory(JSON.parse(params.get('brainParams') || '{}'));
      console.log(`[brain] guard brain: ${brainName}`);
    } else if (brainName !== 'builtin') {
      console.warn(`[brain] Unknown guard brain "${brainName}", keeping built-in guard AI`);
    }
  } else {
    game._guardBrain = GUARD_BRAINS.skirmish(GUARD_V4);
    console.log('[brain] guard brain: v4');
  }

  // Training polygon: ?polygon={"type":"bombardier","share":0.5,"fromWave":1}
  // — the research's skewed waves (see Simulator options.polygon), to look
  // at one archetype's tactics up close. Not the real game: no telemetry.
  const polygon = params.get('polygon');
  if (polygon) {
    globalThis.KOROVANY_TELEMETRY_URL = null;
    game._polygon = JSON.parse(polygon);
    console.log(`[polygon] ${polygon}`);
  }

  // Dev playtest: `?startWave=N` — the smart bot plays waves 1..N−1 headless
  // (seeded Simulator, a few seconds), then the run starts on wave N with its
  // robber (cards, gold, HP, super meter, horse) and deck. Not a real run:
  // no telemetry.
  const startWave = Number(params.get('startWave')) || 0;
  if (startWave > 1) {
    globalThis.KOROVANY_TELEMETRY_URL = null;
    const [{ Simulator }, { SmartPolicy }] = await Promise.all([
      import('./sim/simulator.js'), import('./sim/policies.js'),
    ]);
    const startGame = game.startGame.bind(game);
    game.startGame = () => {
      startGame();
      let snap = null;
      for (let seed = Date.now() % 100000; !snap; seed++) {
        const res = new Simulator(new SmartPolicy(), { seed, maxWaves: startWave, snapshotAtWave: startWave }).run();
        snap = res && res.snapshot;
      }
      game.player = snap.player;
      for (const k of ['cardCounts', 'deck', 'draftSize', 'rerollCount']) game.ui[k] = snap.ui[k];
      game.wave = startWave;
      game.score = snap.score;
      for (const k of ['caravans', 'guards', 'loots', 'pickups', 'projectiles', 'bombs']) game[k] = [];
      game._spawnWave();
      console.log(`[startWave] wave ${startWave}, cards`, snap.ui.cardCounts);
    };
  }

  game.start();

  // Hide splash on the next frame — by that point the renderer has painted
  // at least once, so the user sees game state instead of a flash of nothing.
  requestAnimationFrame(() => {
    const splash = document.getElementById('splash');
    if (splash) splash.remove();
  });

  // Dev playtest: `?rebirth-demo=1` boots straight into the rebirth
  // cutscene so the cinematic can be iterated on without grinding through
  // a real run. Loops by re-entering after `_applyRebirthReset` returns
  // the game to PLAYING — a single `R` keypress restarts the cutscene on
  // demand for quick visual diffing.
  if (params.get('rebirth-demo') === '1') {
    const triggerDemo = () => {
      if (game.state === 'menu' || game.state === 'gameover') game.startGame();
      // startGame puts us in PLAYING with a fresh player; jump to cutscene.
      game.wave = 7; // arbitrary "wave-before" for the subtitle
      game.enterRebirth();
    };
    setTimeout(triggerDemo, 50);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyR' && !e.repeat) triggerDemo();
    });
  }

  // Expose for debugging / manual log export from devtools.
  window.__game = game;

  installFullscreenControls(canvas, game);
  installUpdateReloadHandler(canvas, game);
  installRelicToggleHandler(canvas, game);
  installMenuLinksHandler(canvas, game);

  // Background update detection — sets game.updateAvailable so the main
  // menu can surface a reload prompt without interrupting active play.
  // Also pokes the service worker to fetch the new sw.js right away, so
  // by the time the player clicks the reload banner the new SW has
  // already activated and the navigation routes through the new
  // network-first handler instead of the stale cache-first one.
  startVersionCheck(build.commit, (info) => {
    game.updateAvailable = info;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration()
        .then(reg => reg && reg.update().catch(() => {}))
        .catch(() => {});
    }
  });
}

// Native Fullscreen API toggle. Must run inside a user-gesture handler
// (mousedown/touchstart/keydown) — browsers reject otherwise.
function requestFullscreen() {
  const el = document.documentElement;
  const rfs = el.requestFullscreen
    || el.webkitRequestFullscreen
    || el.msRequestFullscreen;
  if (!rfs) return;
  const p = rfs.call(el);
  if (p && p.then) {
    p.then(() => {
      // Landscape lock only resolves when called from the same gesture
      // that entered fullscreen — mobile screens otherwise stay portrait.
      try { screen.orientation?.lock?.('landscape').catch(() => {}); } catch {}
    }).catch(() => {});
  }
}

function toggleFullscreen() {
  if (document.fullscreenElement) {
    (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
  } else {
    requestFullscreen();
  }
}

function installFullscreenControls(canvas, game) {
  const STATES_WITH_BTN = new Set(['menu', 'paused', 'gameover', 'shop']);

  const hitFullscreenBtn = (clientX, clientY) => {
    const btn = game.ui && game.ui.fullscreenBtn;
    if (!btn || !STATES_WITH_BTN.has(game.state)) return false;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = (clientX - rect.left) * sx;
    const y = (clientY - rect.top) * sy;
    return x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h;
  };

  // Capture phase + stopImmediatePropagation prevents Input's mousedown
  // listener from setting `mouse.clicked`, which would otherwise be read
  // next frame as "advance from menu / continue from gameover".
  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    if (hitFullscreenBtn(e.clientX, e.clientY)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toggleFullscreen();
    }
  }, true);

  canvas.addEventListener('touchstart', (e) => {
    const t = e.changedTouches[0];
    if (t && hitFullscreenBtn(t.clientX, t.clientY)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toggleFullscreen();
    }
  }, { capture: true, passive: false });

  // F: keyboard shortcut. Synchronous in the keydown handler so the
  // browser still sees a valid user gesture for the fullscreen request.
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyF' && !e.repeat && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e)) {
      toggleFullscreen();
    }
  });
}

// Click on the "update available" banner in the main menu reloads the page
// with a cache-busting query so the SW/HTTP cache can't serve the stale
// bundle. Same capture-phase pattern as the fullscreen button — intercepts
// before Input treats the click as "advance from menu".
function installUpdateReloadHandler(canvas, game) {
  const hitUpdateBtn = (clientX, clientY) => {
    const btn = game.ui && game.ui.updateBtn;
    if (!btn || game.state !== 'menu' || !game.updateAvailable) return false;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = (clientX - rect.left) * sx;
    const y = (clientY - rect.top) * sy;
    return x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h;
  };

  // Force-invalidate the HTTP cache for every JS module we loaded, THEN
  // navigate. A plain location.replace doesn't help because nginx serves
  // js/*.js with `Cache-Control: public, immutable, max-age=31536000` —
  // Chrome refuses to revalidate the static module URLs (no version query
  // on the import lines), so the new index.html still pulls stale modules
  // from disk cache. `fetch(url, {cache: 'reload'})` bypasses the cache
  // for that one request AND rewrites the cache entry with the fresh
  // response, so the subsequent navigation finds new bytes on disk.
  //
  // Additionally: trigger a SW update before navigating. Without this,
  // the OLD service worker (cache-first handler) can still be in control
  // when the reload fires, and intercepts the navigation with a stale
  // cache entry. waitForNewSwOrTimeout resolves once the new SW has
  // claimed clients (controllerchange) so the navigation goes through
  // the new network-first handler in sw.js.
  const waitForNewSwOrTimeout = async (ms) => {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      reg.update().catch(() => {});
      await new Promise((resolve) => {
        const t = setTimeout(resolve, ms);
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          clearTimeout(t);
          resolve();
        }, { once: true });
      });
    } catch (_e) {}
  };

  const reload = () => {
    const jsUrls = [...new Set(
      performance.getEntriesByType('resource')
        .map(r => r.name)
        .filter(u => u.endsWith('.js') || u.endsWith('.json'))
    )];
    const refreshAll = Promise.all(
      jsUrls.map(u => fetch(u, { cache: 'reload' }).catch(() => null))
    );
    // Hard timeout: if the network is slow/dead, don't strand the user
    // staring at a button that did nothing — fall through to the reload
    // with whatever subset of modules got refreshed.
    const timeout = new Promise(resolve => setTimeout(resolve, 4000));
    Promise.race([refreshAll, timeout])
      .then(() => waitForNewSwOrTimeout(3000))
      .finally(() => {
        location.replace(`${location.pathname}?v=${Date.now()}${location.hash}`);
      });
  };

  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    if (hitUpdateBtn(e.clientX, e.clientY)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      reload();
    }
  }, true);

  canvas.addEventListener('touchstart', (e) => {
    const t = e.changedTouches[0];
    if (t && hitUpdateBtn(t.clientX, t.clientY)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      reload();
    }
  }, { capture: true, passive: false });
}

// Relic toggle row on the main menu. Same capture-phase + stopImmediate
// trick as fullscreen/update so the menu's "click anywhere = start game"
// handler doesn't fire on a toggle tap. Reads the per-slot hit-rects
// recorded by UI._renderMenuRelics during the last menu render.
function installRelicToggleHandler(canvas, game) {
  const hitRelicSlot = (clientX, clientY) => {
    const slots = game.ui && game.ui.relicSlots;
    if (!slots || slots.length === 0 || game.state !== 'menu') return null;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const x = (clientX - rect.left) * sx;
    const y = (clientY - rect.top) * sy;
    for (const s of slots) {
      if (x >= s.x && x <= s.x + s.w && y >= s.y && y <= s.y + s.h) return s;
    }
    return null;
  };

  const toggle = (slot) => {
    const active = getActiveRelics();
    setRelicActive(slot.id, !active.has(slot.id));
  };

  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    const slot = hitRelicSlot(e.clientX, e.clientY);
    if (slot) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toggle(slot);
    }
  }, true);

  canvas.addEventListener('touchstart', (e) => {
    const t = e.changedTouches[0];
    if (!t) return;
    const slot = hitRelicSlot(t.clientX, t.clientY);
    if (slot) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toggle(slot);
    }
  }, { capture: true, passive: false });
}

// Top-left menu links (privacy policy, telemetry opt-out). Same
// capture-phase interception as the relic slots so the click doesn't
// also start a run. Hit-rects come from UI.renderMenuLinks.
function installMenuLinksHandler(canvas, game) {
  const hitLink = (clientX, clientY) => {
    const links = game.ui && game.ui.menuLinks;
    if (!links || links.length === 0 || game.state !== 'menu') return null;
    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left) * (canvas.width / rect.width);
    const y = (clientY - rect.top) * (canvas.height / rect.height);
    return links.find(l => x >= l.x && x <= l.x + l.w && y >= l.y && y <= l.y + l.h) || null;
  };

  const activate = (link) => {
    if (link.action === 'privacy') window.open('./privacy.html', '_blank', 'noopener');
    else if (link.action === 'telemetry') setTelemetryOptOut(!isTelemetryOptedOut());
    else if (link.action === 'lang') toggleLang();
  };

  const onPointer = (clientX, clientY, e) => {
    const link = hitLink(clientX, clientY);
    if (!link) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    activate(link);
  };

  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 0) onPointer(e.clientX, e.clientY, e);
  }, true);

  canvas.addEventListener('touchstart', (e) => {
    const t = e.changedTouches[0];
    if (t) onPointer(t.clientX, t.clientY, e);
  }, { capture: true, passive: false });
}

// SW registration — file:// origins don't support service workers, so guard
// with location.protocol. Failure is non-fatal: the game still works without
// offline support.
// Local dev (evolution branch): no SW — its cache is keyed by the commit in
// version.json, so uncommitted brain code would be served stale. Drop any
// worker a previous visit registered.
const isLocalDev = ['localhost', '127.0.0.1'].includes(location.hostname);
if ('serviceWorker' in navigator && isLocalDev) {
  navigator.serviceWorker.getRegistrations()
    .then(regs => regs.forEach(r => r.unregister()))
    .catch(() => {});
  if (window.caches) caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
} else if ('serviceWorker' in navigator && location.protocol.startsWith('http')) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch(() => {});
  });
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
